export type PendingTenant = {
  id: string;
  fbo_id: string;
  tail_number: string;
  operator_name: string | null;
  aircraft_type_hint: string | null;
};

export type Aircraft = {
  id: string;
  make: string;
  model: string;
};

export type ResolutionResult = {
  success: boolean;
  message: string;
  tenantCreated?: boolean;
  faaMappingCreated?: boolean;
};

// Use a generic type for postgrest client to avoid version-specific typing issues
type PostgrestClientLike = {
  from: (table: string) => any;
};

export const QuarantineService = {
  /**
   * Resolve quarantine entry with real tail - creates FAA mappings
   * Next FlightBridge sync will create tenant automatically
   */
  async resolveWithFaaMapping(
    postgrest: PostgrestClientLike,
    tenant: PendingTenant,
    aircraft: Aircraft
  ): Promise<ResolutionResult> {
    const manufacturer = aircraft.make.toUpperCase();
    const model = aircraft.model.toUpperCase();

    // Step 1: Check/insert faa_tail_numbers
    const { data: existingTail } = await postgrest
      .from("faa_tail_numbers")
      .select("manufacturer, model")
      .eq("tail_number", tenant.tail_number)
      .maybeSingle();

    let faaManufacturer = manufacturer;
    let faaModel = model;

    if (!existingTail) {
      const { error } = await postgrest.from("faa_tail_numbers").insert({
        tail_number: tenant.tail_number,
        manufacturer,
        model,
      });
      if (error)
        throw new Error(`Failed to create FAA tail entry: ${error.message}`);
    } else {
      faaManufacturer = existingTail.manufacturer;
      faaModel = existingTail.model;
    }

    // Step 2: Check/insert faa_to_airplx mapping
    let faaMappingCreated = false;
    const { data: existingMapping } = await postgrest
      .from("faa_to_airplx")
      .select("id")
      .eq("faa_manufacturer", faaManufacturer)
      .eq("faa_model", faaModel)
      .maybeSingle();

    if (!existingMapping) {
      const { error } = await postgrest.from("faa_to_airplx").insert({
        faa_manufacturer: faaManufacturer,
        faa_model: faaModel,
        matched_aircraft_id: aircraft.id,
      });
      if (error)
        throw new Error(`Failed to create FAA mapping: ${error.message}`);
      faaMappingCreated = true;
    }

    // Step 3: Delete from quarantine
    const { error: deleteError } = await postgrest
      .from("flightbridge_pending_tenants")
      .delete()
      .eq("id", tenant.id);
    if (deleteError)
      throw new Error(
        `Failed to remove from quarantine: ${deleteError.message}`
      );

    return {
      success: true,
      message: `Mapped ${tenant.tail_number} → ${aircraft.make} ${aircraft.model}`,
      faaMappingCreated,
    };
  },

  /**
   * Resolve quarantine entry as FBO user - creates tenant + queue entry
   * No FAA mapping created, tail goes to approval queue for super admin
   */
  async resolveForFboUser(
    postgrest: PostgrestClientLike,
    tenant: PendingTenant,
    aircraft: Aircraft,
    userId: string
  ): Promise<ResolutionResult> {
    // Step 1: Create tenant with original tail number
    const { data: newTenant, error: tenantError } = await postgrest
      .from("tenant")
      .insert({
        fbo_id: tenant.fbo_id,
        tail_number: tenant.tail_number,
        aircraft_id: aircraft.id,
      })
      .select("id")
      .single();

    if (tenantError)
      throw new Error(`Failed to create tenant: ${tenantError.message}`);

    // Step 2: Add to FAA approval queue for super admin review
    const { error: queueError } = await postgrest
      .from("pending_faa_approvals")
      .insert({
        tail_number: tenant.tail_number,
        aircraft_id: aircraft.id,
        fbo_id: tenant.fbo_id,
        tenant_id: newTenant.id,
        created_by: userId,
      });

    if (queueError)
      throw new Error(`Failed to queue for approval: ${queueError.message}`);

    // Step 3: Delete from quarantine
    const { error: deleteError } = await postgrest
      .from("flightbridge_pending_tenants")
      .delete()
      .eq("id", tenant.id);

    if (deleteError)
      throw new Error(
        `Failed to remove from quarantine: ${deleteError.message}`
      );

    return {
      success: true,
      message: `Created tenant for ${tenant.tail_number} - pending FAA approval`,
      tenantCreated: true,
    };
  },

  /**
   * Resolve quarantine entry with fake/quote tail - creates tenant directly
   * No FAA entries created, tail is mangled for uniqueness
   */
  async resolveWithDirectTenant(
    postgrest: PostgrestClientLike,
    tenant: PendingTenant,
    aircraft: Aircraft
  ): Promise<ResolutionResult> {
    // Mangle tail for uniqueness: "001" → "001-Q-a1b2c3"
    const mangledTail = `${tenant.tail_number}-Q-${tenant.id.substring(0, 6)}`;

    // Step 1: Create tenant directly
    const { error: tenantError } = await postgrest.from("tenant").insert({
      fbo_id: tenant.fbo_id,
      tail_number: mangledTail,
      aircraft_id: aircraft.id,
    });
    if (tenantError)
      throw new Error(`Failed to create tenant: ${tenantError.message}`);

    // Step 2: Delete from quarantine
    const { error: deleteError } = await postgrest
      .from("flightbridge_pending_tenants")
      .delete()
      .eq("id", tenant.id);
    if (deleteError)
      throw new Error(
        `Failed to remove from quarantine: ${deleteError.message}`
      );

    return {
      success: true,
      message: `Created tenant ${mangledTail} → ${aircraft.make} ${aircraft.model}`,
      tenantCreated: true,
    };
  },

  /**
   * Fetch all pending tenants (unresolved quarantine entries)
   */
  async fetchPendingTenants(postgrest: PostgrestClientLike) {
    const { data, error } = await postgrest
      .from("flightbridge_pending_tenants")
      .select(
        "id, fbo_id, tail_number, operator_name, aircraft_type_hint, source, created_at, resolved_at, notes, fbo:fbo_id(name)"
      )
      .is("resolved_at", null)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Delete pending tenants (for "Remove & Retry" action)
   */
  async deletePendingTenants(
    postgrest: PostgrestClientLike,
    ids: string[]
  ) {
    const { error } = await postgrest
      .from("flightbridge_pending_tenants")
      .delete()
      .in("id", ids);

    if (error) throw new Error(error.message);
  },
};
